import React from 'react';

import { useRecoilState } from 'recoil';
import { outletAtom } from 'core/config/atoms';

import moment from "moment";
import QRCode from "react-qr-code";

const { ToWords } = require('to-words');

export const PrintBooking = React.forwardRef((props, ref) => {
    const [outlet] = useRecoilState(outletAtom);
    const data = props?.payload?.data;

    const toWords = new ToWords({
        localeCode: 'en-IN',
        converterOptions: {
            currency: true,
            ignoreDecimal: false,
            ignoreZeroCurrency: false,
            doNotAddOnly: false,
            currencyOptions: {
                name: 'Rupee',
                plural: 'Rupees',
                symbol: '₹',
                fractionalUnit: { name: 'Paisa', plural: 'Paise', symbol: '' },
            },
        },
    });

    const getDifference = (data) => {
        if (!data?.checkIn || !data?.checkOut) return "NA";
        const checkIn = new Date(data.checkIn);
        const checkOut = new Date(data.checkOut);
        if (isNaN(checkIn.getTime()) || isNaN(checkOut.getTime())) return "NA";
        const diffTime = checkOut.getTime() - checkIn.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : "NA";
    };

    const getTax = () => {
        return (data.finaltotal - (data.finaltotal / (1 + (data.tax / 100)))).toFixed(2);
    }

    if (!props.isPrint || !data) return null;

    return (
        <div className="mx-auto p-4 bg-white text-ascent font-sans" ref={ref}>
            <div className="max-w-4xl mx-auto border border-gray-200 shadow-sm p-8">
                <div className="flex justify-between items-start border-b-2 border-prime pb-8">
                    <div>
                        <h1 className="text-4xl font-bold text-prime tracking-tight">INVOICE</h1>
                        <p className="text-sm text-gray-500 mt-1">Booking Confirmed</p>
                    </div>
                    <div className="text-right">
                        <h2 className="text-xl font-bold text-ascent">{outlet.outletid.name}</h2>
                        <div className="text-sm text-gray-600 mt-2 space-y-1">
                            <p>{outlet.outletid.addresses[0].street_1}</p>
                            <p>{outlet.outletid.addresses[0].street_2}</p>
                            <p>{outlet.outletid.addresses[0].city}, {outlet.outletid.addresses[0].state}</p>
                            <p>{outlet.outletid.addresses[0].postal_code}</p>
                            <p className="mt-2 text-ascent font-medium">Phone: {outlet.outletid.phone}</p>
                            <p className="text-ascent font-medium">Email: {outlet.outletid.email}</p>
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-8 py-8 border-b border-gray-200">
                    <div>
                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Bill To</h3>
                        <p className="text-lg font-bold text-prime">{data.bookName}</p>
                        <p className="text-sm text-gray-600">{data.bookEmail}</p>
                        <p className="text-sm text-gray-600">{data.bookPhone}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Invoice Number</h3>
                            <p className="text-sm font-semibold text-ascent">{data.bookID}</p>
                        </div>
                        <div>
                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Date</h3>
                            <p className="text-sm font-semibold text-ascent">{moment(data.createdAt).format("DD MMM, YYYY")}</p>
                        </div>
                        <div>
                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Payment Status</h3>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium uppercase ${data.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                {data.paymentStatus}
                            </span>
                        </div>
                        <div>
                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Payment Mode</h3>
                            <p className="text-sm font-semibold text-ascent">{data.paymentMode || 'N/A'}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-prime/5 rounded-lg p-4 my-6 grid grid-cols-4 gap-4 text-center border border-prime/10">
                    <div>
                        <p className="text-xs text-ascent/70 uppercase">Check-In</p>
                        <p className="font-bold text-prime">{moment(data.checkIn).format("DD MMM YYYY")}</p>
                    </div>
                    <div>
                        <p className="text-xs text-ascent/70 uppercase">Check-Out</p>
                        <p className="font-bold text-prime">{moment(data.checkOut).format("DD MMM YYYY")}</p>
                    </div>
                    <div>
                        <p className="text-xs text-ascent/70 uppercase">Duration</p>
                        <p className="font-bold text-prime">{getDifference(data)} Nights</p>
                    </div>
                    <div>
                        <p className="text-xs text-ascent/70 uppercase">Guests / Rooms</p>
                        <p className="font-bold text-prime">{data.nop} Guests / {data.nor} Rooms</p>
                    </div>
                </div>
                <table className="w-full text-sm text-left mb-8">
                    <thead className="text-xs text-ascent uppercase border-b-2 border-prime">
                        <tr>
                            <th className="py-3 font-medium">Description</th>
                            <th className="py-3 font-medium text-right">Rate</th>
                            <th className="py-3 font-medium text-center">Tax %</th>
                            <th className="py-3 font-medium text-right">Amount</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        <tr>
                            <td className="py-4">
                                <p className="font-medium text-ascent">Room Charges</p>
                                <p className="text-xs text-gray-500">Accommodation for {data.nop} persons in {data.nor} rooms for {getDifference(data)} nights.</p>
                                {data.bookType && <p className="text-xs text-gray-500 capitalize">Type: {data.bookType}</p>}
                            </td>
                            <td className="py-4 text-right">₹{data.total}</td>
                            <td className="py-4 text-center">{data.tax}%</td>
                            <td className="py-4 text-right">₹{data.total}</td>
                        </tr>
                    </tbody>
                </table>
                <div className="flex justify-between items-start border-t border-gray-200 pt-8">
                    <div className="w-1/2">
                        {
                            outlet.outletid.upi && (
                                <div className="flex items-center space-x-4 border border-prime/20 rounded p-4 bg-prime/5 w-fit">
                                    <div className="bg-white p-2 rounded border border-prime/10">
                                        <QRCode size={80} style={{ height: "auto", maxWidth: "100%", width: "100%" }} value={`upi://pay?pa=${outlet.outletid.upi}&pn=${outlet.outletid.name}&cu=INR`} viewBox={`0 0 256 256`} />
                                    </div>
                                    <div className="text-sm">
                                        <p className="font-bold text-prime">Scan to Pay</p>
                                        <p className="text-gray-500 text-xs mt-1">UPI ID: {outlet.outletid.upi}</p>
                                    </div>
                                </div>
                            )
                        }
                        <div className="mt-6">
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Amount in Words</h4>
                            <p className="text-sm text-ascent italic capitalize">
                                {toWords.convert(data.finaltotal)} only
                            </p>
                        </div>
                    </div>
                    <div className="w-1/3 space-y-3">
                        <div className="flex justify-between text-sm text-gray-600">
                            <span>Subtotal</span>
                            <span>₹{data.total}</span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-600">
                            <span>Discount</span>
                            <span>- ₹{data.discount}</span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-600">
                            <span>Extra Charges</span>
                            <span>+ ₹{data.extraCharge}</span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-600">
                            <span>Tax</span>
                            <span>+ ₹{getTax()}</span>
                        </div>
                        <div className="flex justify-between items-center text-lg font-bold text-prime pt-3 border-t border-gray-200">
                            <span>Grand Total</span>
                            <span>₹{data.finaltotal}</span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-600 pt-2">
                            <span>Amount Paid</span>
                            <span>₹{data.paid}</span>
                        </div>
                        <div className="flex justify-between items-center text-base font-bold text-ascent pt-2 border-t border-gray-200">
                            <span>Balance Due</span>
                            <span>₹{Math.max(0, data.finaltotal - data.paid)}</span>
                        </div>
                    </div>
                </div>
                <div className="mt-12 pt-8 border-t border-gray-200 text-center text-xs text-gray-500">
                    <p>Thank you for choosing {outlet.outletid.name}. We hope to see you again soon!</p>
                    <p className="mt-2">This is a computer-generated invoice and does not require a physical signature.</p>
                </div>
            </div>
        </div>
    );
});
